import logging from "utils/logging";
import utils from "./firebaseUtils";
import { doc, updateDoc, getDoc, writeBatch, serverTimestamp } from "firebase/firestore";
import { ensureFirebaseAuth } from "../auth/ensureFirebaseAuth";
import {
  FIREBASE_COLLECTION_GAMES,
  FIREBASE_COLLECTION_QUESTIONS,
  FIREBASE_FUNCTION_UPDATE_CATEGORY,
} from "./constants";

const log = logging.getLogger("firebase-editor-funcs");

const updateGameName = async (gameId, newName) => {
  await ensureFirebaseAuth();
  const gameRef = doc(utils.db(), FIREBASE_COLLECTION_GAMES, gameId);
  await updateDoc(gameRef, { name: newName });
};

const updateCategory = async (gameId, roundId, categoryIndex, newCategory) => {
  await ensureFirebaseAuth();
  const editCategory = utils.getCallableFunction(FIREBASE_FUNCTION_UPDATE_CATEGORY);
  const result = await editCategory({
    gameId,
    roundId,
    categoryIndex,
    newCategory,
  });
  log.info("Got result from updating category: ", result);
  // Touch lastTouchedAt on the game doc
  const gameRef = doc(utils.db(), FIREBASE_COLLECTION_GAMES, gameId);
  await updateDoc(gameRef, { lastTouchedAt: serverTimestamp() });
  return result;
};

const updateFinalCategory = async (gameId, newCategory) => {
  await ensureFirebaseAuth();
  const gameRef = doc(utils.db(), FIREBASE_COLLECTION_GAMES, gameId);

  await updateDoc(gameRef, {
    "categories.final.category": newCategory,
    lastTouchedAt: serverTimestamp(),
  });

  const gameSnap = await getDoc(gameRef);
  const finalQuestionId = gameSnap.get("categories.final.question");

  if (finalQuestionId) {
    const questionRef = doc(
      utils.db(),
      FIREBASE_COLLECTION_GAMES,
      gameId,
      FIREBASE_COLLECTION_QUESTIONS,
      finalQuestionId
    );
    await updateDoc(questionRef, { category: newCategory });
  }
};

const updateQuestion = async (gameId, questionId, newQuestionData) => {
  await ensureFirebaseAuth();
  const questionRef = doc(
    utils.db(),
    FIREBASE_COLLECTION_GAMES,
    gameId,
    FIREBASE_COLLECTION_QUESTIONS,
    questionId
  );
  await updateDoc(questionRef, {
    ...newQuestionData,
    updatedAt: serverTimestamp(),
  });
  // Touch lastTouchedAt on the game doc
  const gameRef = doc(utils.db(), FIREBASE_COLLECTION_GAMES, gameId);
  await updateDoc(gameRef, { lastTouchedAt: serverTimestamp() });
};

const updateGameDetails = async (gameId, newDetails) => {
  await ensureFirebaseAuth();
  const gameRef = doc(utils.db(), FIREBASE_COLLECTION_GAMES, gameId);
  await updateDoc(gameRef, newDetails);
};

/**
 * Reset every clue in a round's category column to empty. Question docs carry
 * no roundId/categoryIndex fields, so the question IDs are read off the game
 * doc's category map (categories[roundId][categoryIndex]) and batch-cleared.
 */
const eraseRoundCategoryClues = async (gameId, roundId, categoryIndex) => {
  await ensureFirebaseAuth();
  const gameRef = doc(utils.db(), FIREBASE_COLLECTION_GAMES, gameId);
  const gameSnap = await getDoc(gameRef);
  const roundData = gameSnap.get(`categories.${roundId}`);
  // Missing round/column is an anomaly (the UI offered erase for a column it
  // believed existed) — throw so the caller surfaces it instead of reporting a
  // false success. An empty column is a legitimate quiet no-op.
  if (!roundData) {
    log.error("eraseRoundCategoryClues: round not found", { gameId, roundId, categoryIndex });
    throw new Error(`Round "${roundId}" not found on game ${gameId}`);
  }

  const column = Array.isArray(roundData)
    ? roundData[categoryIndex]
    : Object.values(roundData)[categoryIndex];
  if (!column) {
    log.error("eraseRoundCategoryClues: category column not found", { gameId, roundId, categoryIndex });
    throw new Error(`Category column ${categoryIndex} not found in round "${roundId}"`);
  }

  const questionIds = [1, 2, 3, 4, 5]
    .map((row) => column[String(row)])
    .filter(Boolean);
  if (questionIds.length === 0) return;

  const batch = writeBatch(utils.db());
  questionIds.forEach((qid) => {
    const questionRef = doc(
      utils.db(),
      FIREBASE_COLLECTION_GAMES,
      gameId,
      FIREBASE_COLLECTION_QUESTIONS,
      qid
    );
    batch.update(questionRef, {
      clue: "",
      answers: [],
      prompt: "",
      dailyDouble: false,
      clueType: "text",
      updatedAt: serverTimestamp(),
    });
  });
  await batch.commit();
  await updateDoc(gameRef, { lastTouchedAt: serverTimestamp() });
};

const api = {
  updateGameName,
  updateCategory,
  updateFinalCategory,
  updateQuestion,
  updateGameDetails,
  eraseRoundCategoryClues,
};

export default api;
